// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
import type { AppContext } from '../app';
import { el } from '../dom';
import { formatMass } from '../format';
import { divisionColor, substanceCategoryColor } from '../colors';

export function renderFlow(root: HTMLElement, ctx: AppContext) {
  root.appendChild(el('h1', { class: 'section-title' }, 'Industry → substance flow'));
  root.appendChild(el('p', { class: 'section-sub' }, [
    'Which industry divisions emit which categories of substances. Ribbon width = lifetime kg. Hover any ribbon to highlight one industry-substance pair.'
  ]));

  // Aggregate sector_substance into division × substance-category
  const { sectorSubstance, industries, substances } = ctx.data;
  const subCatById = new Map<number, string>(substances.map(s => [s.id, s.category]));
  // Build matrix: division → category → kg
  const matrix = new Map<string, Map<string, number>>();
  for (const row of sectorSubstance) {
    const cat = subCatById.get(row.substance) ?? 'Other';
    let m = matrix.get(row.division);
    if (!m) { m = new Map(); matrix.set(row.division, m); }
    m.set(cat, (m.get(cat) || 0) + row.kg);
  }

  // Filter to divisions present in industries list (with metadata)
  const divisionList = industries.divisions.filter(d => matrix.has(d.code));
  // Order by total descending
  divisionList.sort((a, b) => b.total - a.total);

  // All categories (ordered)
  const categoryOrder = [
    'Criteria air pollutants',
    'Particulates',
    'Heavy metals',
    'Organics & VOCs',
    'Acid gases & inorganics',
    'Persistent organic pollutants',
    'Other inorganics',
    'Other'
  ];
  // Filter to categories with data
  const categories = categoryOrder.filter(c =>
    divisionList.some(d => (matrix.get(d.code)?.get(c) ?? 0) > 0)
  );

  // SVG dimensions
  const W = 1000, H = 520, padding = 40;
  const leftX = padding, rightX = W - padding - 8;
  const bandH = H - padding * 2;
  // Each side height proportional to sums.
  const totalLeft = divisionList.reduce((a, d) => a + d.total, 0);
  const totalRight = categories.reduce((acc, c) =>
    acc + divisionList.reduce((s, d) => s + (matrix.get(d.code)?.get(c) ?? 0), 0)
  , 0);

  // Compute y positions for each division block (left)
  const leftBlocks = new Map<string, { y: number; h: number; remainingY: number }>();
  let acc = 0;
  for (const d of divisionList) {
    const h = (d.total / totalLeft) * bandH;
    leftBlocks.set(d.code, { y: padding + acc, h, remainingY: padding + acc });
    acc += h;
  }

  // Compute y positions for each category block (right)
  const rightBlocks = new Map<string, { y: number; h: number; remainingY: number }>();
  acc = 0;
  for (const c of categories) {
    const total = divisionList.reduce((s, d) => s + (matrix.get(d.code)?.get(c) ?? 0), 0);
    const h = (total / totalRight) * bandH;
    rightBlocks.set(c, { y: padding + acc, h, remainingY: padding + acc });
    acc += h;
  }

  // Build SVG
  const ns = 'http://www.w3.org/2000/svg';
  const svgEl = document.createElementNS(ns, 'svg');
  svgEl.setAttribute('viewBox', `0 0 ${W} ${H}`);
  svgEl.setAttribute('class', 'chart');
  svgEl.setAttribute('preserveAspectRatio', 'xMidYMid meet');

  // Background bands
  for (const d of divisionList) {
    const block = leftBlocks.get(d.code)!;
    const rect = document.createElementNS(ns, 'rect');
    rect.setAttribute('x', String(leftX));
    rect.setAttribute('y', String(block.y));
    rect.setAttribute('width', '12');
    rect.setAttribute('height', String(block.h));
    rect.setAttribute('fill', divisionColor(d.code));
    svgEl.appendChild(rect);
    const text = document.createElementNS(ns, 'text');
    text.setAttribute('x', String(leftX - 6));
    text.setAttribute('y', String(block.y + block.h / 2));
    text.setAttribute('text-anchor', 'end');
    text.setAttribute('dominant-baseline', 'middle');
    text.setAttribute('font-size', '11');
    text.setAttribute('fill', 'var(--text-secondary)');
    text.textContent = block.h > 12 ? d.name : '';
    svgEl.appendChild(text);
  }
  for (const c of categories) {
    const block = rightBlocks.get(c)!;
    const rect = document.createElementNS(ns, 'rect');
    rect.setAttribute('x', String(rightX));
    rect.setAttribute('y', String(block.y));
    rect.setAttribute('width', '12');
    rect.setAttribute('height', String(block.h));
    rect.setAttribute('fill', substanceCategoryColor(c));
    svgEl.appendChild(rect);
    const text = document.createElementNS(ns, 'text');
    text.setAttribute('x', String(rightX + 16));
    text.setAttribute('y', String(block.y + block.h / 2));
    text.setAttribute('text-anchor', 'start');
    text.setAttribute('dominant-baseline', 'middle');
    text.setAttribute('font-size', '11');
    text.setAttribute('fill', 'var(--text-secondary)');
    text.textContent = block.h > 12 ? c : '';
    svgEl.appendChild(text);
  }

  // Ribbons — for each (division, category) draw a cubic bezier band
  const tooltip = document.createElement('div');
  tooltip.className = 'glossary-tooltip';
  tooltip.hidden = true;
  document.body.appendChild(tooltip);

  for (const d of divisionList) {
    const m = matrix.get(d.code)!;
    const left = leftBlocks.get(d.code)!;
    // For consistent ordering, iterate categories in display order.
    for (const c of categories) {
      const kg = m.get(c) ?? 0;
      if (kg <= 0) continue;
      const right = rightBlocks.get(c)!;
      const leftH = (kg / d.total) * left.h;
      const rightTotal = divisionList.reduce((s, dd) => s + (matrix.get(dd.code)?.get(c) ?? 0), 0);
      const rightH = (kg / rightTotal) * right.h;
      const lY0 = left.remainingY;
      const lY1 = lY0 + leftH;
      const rY0 = right.remainingY;
      const rY1 = rY0 + rightH;
      left.remainingY = lY1;
      right.remainingY = rY1;

      const path = document.createElementNS(ns, 'path');
      const cpX = (leftX + 12 + rightX) / 2;
      const x0 = leftX + 12;
      const x1 = rightX;
      const dStr = `M ${x0} ${lY0} C ${cpX} ${lY0}, ${cpX} ${rY0}, ${x1} ${rY0} L ${x1} ${rY1} C ${cpX} ${rY1}, ${cpX} ${lY1}, ${x0} ${lY1} Z`;
      path.setAttribute('d', dStr);
      path.setAttribute('fill', divisionColor(d.code));
      path.setAttribute('fill-opacity', '0.4');
      path.setAttribute('stroke', 'none');
      path.style.transition = 'fill-opacity 150ms ease';
      path.addEventListener('mouseenter', (ev) => {
        path.setAttribute('fill-opacity', '0.85');
        tooltip.hidden = false;
        tooltip.innerHTML = `<strong>${d.name}</strong> → <strong>${c}</strong><br/>${formatMass(kg)}`;
        positionTooltip(tooltip, ev as MouseEvent);
      });
      path.addEventListener('mousemove', (ev) => positionTooltip(tooltip, ev as MouseEvent));
      path.addEventListener('mouseleave', () => {
        path.setAttribute('fill-opacity', '0.4');
        tooltip.hidden = true;
      });
      svgEl.appendChild(path);
    }
  }

  const card = el('div', { class: 'card' });
  card.appendChild(svgEl);
  root.appendChild(card);

  // Legend
  const legendCats = el('div', { class: 'legend mt-lg' },
    categories.map(c => el('span', null, [
      el('span', { class: 'legend-swatch', style: `background:${substanceCategoryColor(c)}` }),
      c
    ]))
  );
  root.appendChild(legendCats);
}

function positionTooltip(el: HTMLElement, ev: MouseEvent) {
  el.style.left = `${ev.clientX + 12 + window.scrollX}px`;
  el.style.top = `${ev.clientY + 16 + window.scrollY}px`;
}
